import { ExternalLink, Smartphone, AlertTriangle } from 'lucide-react';

interface TestingAccessInstructionsProps {
  googlePlayInternalTestLink?: string | null;
  jiraKey: string;
  compact?: boolean;
}

/**
 * IMA-14: Google Play internal test link + TestFlight iOS instructions for customers.
 */
export function TestingAccessInstructions({
  googlePlayInternalTestLink,
  jiraKey,
  compact = false,
}: TestingAccessInstructionsProps) {
  const playLink = googlePlayInternalTestLink?.trim() || null;

  return (
    <div className={`space-y-4 ${compact ? '' : 'mb-6'}`}>
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Smartphone className="h-5 w-5 text-emerald-700 flex-shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <h4 className="font-medium text-emerald-900 mb-1">Android — Google Play Internal Testing</h4>
            {playLink ? (
              <a
                href={playLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-800 underline break-all hover:text-emerald-950"
              >
                Open Google Play test link
                <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
              </a>
            ) : (
              <p className="text-sm text-emerald-800">
                Your Google Play test link is not available yet. We&apos;ll add it here once internal testing is ready.
              </p>
            )}
            <div className="mt-3 flex items-start gap-2 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-md p-3">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <p>
                If the Google account email you use on your Android device was{' '}
                <strong>not</strong> submitted as a tester, you may not be able to access this link.
                Contact us if you need your tester email list updated.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-sky-50 border border-sky-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Smartphone className="h-5 w-5 text-sky-700 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-sky-900 mb-1">iOS — TestFlight</h4>
            <ol className="text-sm text-sky-900 space-y-1.5 list-decimal list-inside">
              <li>
                Install{' '}
                <a
                  href="https://apps.apple.com/app/testflight/id899247664"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-medium"
                >
                  TestFlight
                </a>{' '}
                from the App Store on your iPhone or iPad.
              </li>
              <li>Check the email inbox for the Apple ID you use on that device for a TestFlight invitation.</li>
              <li>Tap <strong>View in TestFlight</strong>, accept the invite, then install the app from TestFlight.</li>
            </ol>
            <p className="mt-2 text-sm text-sky-800">
              Need more help? See the{' '}
              <a href={`/project/${jiraKey}/help`} className="underline font-medium">
                project help center
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
